import React from 'react'
import { getHexColor } from '@/atoms/utils'
import { NamedColor } from '@/constants/types'
import { useTranslate } from '@/utils/translate/translate-client'
import { HlsTooltip } from './HlsTooltip'

/** AirPlay icon — from Bitmovin skin-modern/images/airplay.svg (32x32 viewBox). */
const AirPlayIcon: React.FC<{ color: NamedColor }> = ({ color }) => {
  const hex = getHexColor(color)

  return (
    <svg width={24} height={24} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill={hex}
        d="M27.1 5.3H4.9c-1.3 0-2.4 1.1-2.4 2.4v11.9c0 1.3 1.1 2.4 2.4 2.4h3.6v-2.4H4.9V7.7h22.1v11.9h-3.6v2.4h3.6c1.3 0 2.4-1.1 2.4-2.4V7.7c0-1.3-1-2.4-2.3-2.4z"
      />
      <path
        fill={hex}
        d="M22.9 24.8L17 16.5c-.2-.2-.6-.5-1-.5-.4 0-.7.2-1 .5l-5.9 8.3c-.2.4-.2.8-.1 1.2.2.4.6.6 1.1.6h11.9c.5 0 .8-.2 1.1-.6.1-.1.1-.2.1-.5-.1-.3-.2-.5-.3-.7zM12.3 24.3l3.7-5.1 3.7 5.1H12.3z"
      />
    </svg>
  )
}

type AirPlayState = 'unavailable' | 'available' | 'connected'

interface HlsAirPlayButtonProps {
  airPlayState: AirPlayState
  onShowPicker: () => void
}

/**
 * Apple AirPlay toggle button for the control bar.
 * Only renders when AirPlay is supported (Apple devices with Safari).
 * Opens the native AirPlay device picker on click.
 */
export const HlsAirPlayButton: React.FC<HlsAirPlayButtonProps> = ({ airPlayState, onShowPicker }) => {
  const { t } = useTranslate('common')

  if (airPlayState === 'unavailable') return null

  const isActive = airPlayState === 'connected'

  return (
    <button
      type="button"
      className="group relative flex h-9 w-9 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20"
      onClick={onShowPicker}
      aria-label={t('airPlay', 'AirPlay')}
    >
      <AirPlayIcon color={isActive ? 'oxide-bright' : 'white'} />
      <HlsTooltip
        title={t('airPlay', 'AirPlay')}
        subtitle={
          isActive ? t('airPlayDisconnect', 'Stop AirPlay.') : t('airPlayConnect', 'Stream to an Apple device.')
        }
      />
    </button>
  )
}
